import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Search,
  BookOpen,
  Calendar
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { studentService } from '../../services/studentService';
import { Student } from '../../types';
import toast from 'react-hot-toast';
import { StudentImportForm } from './StudentImportForm';

interface StudentManagementProps {}

export const StudentManagement: React.FC<StudentManagementProps> = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  console.log(user);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const studentsData = await studentService.getStudents(user?.id);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('students.loadError'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  const handleSubmitStudent = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Helper function to clean form values
    const cleanValue = (value: string | null) => {
      const trimmed = value?.trim();
      return trimmed && trimmed.length > 0 ? trimmed : undefined;
    };
    
    const studentData = {
      name: formData.get('name') as string,
      grade: cleanValue(formData.get('grade') as string) || '',
      className: cleanValue(formData.get('className') as string) || '',
      schoolYear: cleanValue(formData.get('schoolYear') as string) || '',
      status: (formData.get('status') as 'active' | 'inactive' | 'transferred') || 'active',
      notes: cleanValue(formData.get('notes') as string) || '',
      teacherId: user?.id || ''
    };

    if (editingStudent) {
      await handleUpdateStudent(editingStudent.id, studentData);
    } else {
      await handleAddStudent(studentData);
    }
  };

  const handleAddStudent = async (studentData: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setFormSubmitting(true);
      const newStudent = await studentService.addStudent({
        ...studentData,
        teacherId: user?.id || '',
        status: 'active',
        enrollmentDate: new Date()
      });
      
      setStudents(prev => [...prev, newStudent]);
      setShowAddModal(false);
      toast.success(t('students.addSuccess'));
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error(t('students.addError'));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleUpdateStudent = async (id: string, updates: Partial<Student>) => {
    try {
      setFormSubmitting(true);
      await studentService.updateStudent(id, updates);
      setStudents(prev => prev.map(student => 
        student.id === id ? { ...student, ...updates } : student
      ));
      setEditingStudent(null);
      toast.success(t('students.updateSuccess'));
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error(t('students.updateError'));
    } finally {
      setFormSubmitting(false);
    }
  };



  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = student.className?.toLowerCase().includes(selectedClass.toLowerCase());
    const matchesSchoolYear = student.schoolYear?.toLowerCase().includes(selectedSchoolYear.toLowerCase());
    
    return matchesSearch && matchesClass && matchesSchoolYear;
  });



  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm(t('students.confirmDelete'))) {
      return;
    }

    try {
      await studentService.deleteStudent(id);
      setStudents(prev => prev.filter(student => student.id !== id));
      toast.success(t('students.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error(t('students.deleteError'));
    }
  };





  const getSchoolYearName = (schoolYear?: string) => {
    if (!schoolYear) return t('students.noSchoolYear');
    return schoolYear;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('students.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('students.management')}</h1>
          <p className="text-gray-600 mt-1">{t('students.manageStudents')}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Users className="w-4 h-4" />
            <span>{t('students.importStudents')}</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('students.addStudent')}</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{t('students.totalStudents')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{students.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{t('students.assignedToClass')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {students.filter(s => s.className).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{t('students.activeStudents')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {students.filter(s => s.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:items-center sm:justify-between">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('students.searchStudents')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            
            <input
              type="text"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              placeholder={t('students.searchClass')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            
            <input
              type="text"
              value={selectedSchoolYear}
              onChange={(e) => setSelectedSchoolYear(e.target.value)}
              placeholder={t('students.searchSchoolYear')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
            {t('students.showingResults', { count: filteredStudents.length, total: students.length })}
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredStudents.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('students.noStudentsFound')}</h3>
            <p className="text-gray-600">{t('students.noStudentsMatchFilters')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredStudents.map((student) => (
              <div key={student.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <h4 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                        {student.name}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium self-start sm:self-center ${
                        student.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : student.status === 'inactive'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {t(`students.status.${student.status}`)}
                      </span>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-2">
                        <p className="text-xs sm:text-sm text-gray-600">
                          {t('students.grade')}: {student.grade || t('students.noGrade')}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {t('students.class')}: {student.className || t('students.noClass')}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600">
                          {t('students.schoolYear')}: {getSchoolYearName(student.schoolYear)}
                        </p>
                      </div>
                    </div>
                    
                    {student.notes && (
                      <p className="text-xs sm:text-sm text-gray-500 mt-2 italic">
                        {student.notes}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center sm:justify-end space-x-2 sm:ml-4">
                    <button
                      onClick={() => setEditingStudent(student)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('students.editStudent')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteStudent(student.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title={t('students.deleteStudent')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Student Modal */}
      {(showAddModal || editingStudent) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingStudent ? t('students.editStudent') : t('students.addStudent')}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingStudent(null);
                }}
                disabled={formSubmitting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitStudent}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    defaultValue={editingStudent?.name || ''}
                    required
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.grade')}
                  </label>
                  <input
                    type="text"
                    id="grade"
                    name="grade"
                    defaultValue={editingStudent?.grade || ''}
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label htmlFor="className" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.class')}
                  </label>
                  <input
                    type="text"
                    id="className"
                    name="className"
                    defaultValue={editingStudent?.className || ''}
                    placeholder="e.g., Class A, Math 101, Room 205"
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label htmlFor="schoolYear" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.schoolYear')}
                  </label>
                  <input
                    type="text"
                    id="schoolYear"
                    name="schoolYear"
                    defaultValue={editingStudent?.schoolYear || ''}
                    placeholder="e.g., 2024-2025, Academic Year 2024, Fall 2024"
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.statusText')}
                  </label>
                  <select
                    id="status"
                    name="status"
                    defaultValue={editingStudent?.status || 'active'}
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="active">{t('students.status.active')}</option>
                    <option value="inactive">{t('students.status.inactive')}</option>
                    <option value="transferred">{t('students.status.transferred')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('students.notes')}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    defaultValue={editingStudent?.notes || ''}
                    rows={3}
                    disabled={formSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-3 mt-6">
                <button
                  type="button"
                  disabled={formSubmitting}
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingStudent(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {formSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('students.saving')}</span>
                    </>
                  ) : (
                    <span>{editingStudent ? t('common.save') : t('students.addStudent')}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unified Student Import Form */}
      <StudentImportForm
        showModal={showImportModal}
        setShowModal={setShowImportModal}
        onStudentsImported={(importedStudents) => {
          setStudents(prev => [...prev, ...importedStudents]);
        }}
        onClose={() => {
          // Modal closed, no cleanup needed
        }}
      />
    </div>
  );
};
